import { FC, useMemo } from "react";
import { t } from "i18next";
import { useSelector } from "react-redux";
import { useOutletContext } from "react-router";

import { Skeleton } from "@components/shared/Skeleton/Skeleton";
import { DECIMAL_PLACES } from "@constants/numbers";
import { DataTypes } from "@enums/shared.enum";
import { useBooking } from "@hooks/useBooking/useBooking";
import { usePricing } from "@hooks/usePricing/usePricing";
import { ResourceDurationPropType } from "@models/components/booking.model";
import { getLocationType, locationType } from "@models/services/organisation";
import { applyLocalization } from "@services/common.service";
import { RootState } from "@store/index";

const ResourceDuration: FC<ResourceDurationPropType> = ({
  isBookingDisable,
  priceLoader,
  resource,
  selectSlotFromDrawer,
  showResourceTitle = true,
}) => {
  const { resourcePrices, selectedTime } = useSelector(
    (State: RootState) => State.booking
  );
  const {
    disableResource,
    getAvailableDurations,
    resourceActiveClass,
    selectSlot,
  } = useBooking();

  const context: [getLocationType, locationType] = useOutletContext();

  const { getPriceByResourceSlot } = usePricing();

  const isFinanceConfigured = context[1]?.financeConfigured || false;

  const { durations, warningMessage } = useMemo(
    () => getAvailableDurations(resource),
    [resource, selectedTime]
  );

  const handleSlotClick = selectSlotFromDrawer || selectSlot;

  return (
    <div className="fg-unit-det">
      <div
        className={`fg-unit-det-wrap ${disableResource(isBookingDisable, durations)}`}
        data-testid="units-block-test"
      >
        {showResourceTitle && <h5>{applyLocalization(resource?.title)}</h5>}

        <div className="fg-unit-slot-select">
          {durations.map(item => {
            const priceDetails = getPriceByResourceSlot(
              resourcePrices,
              resource.id,
              item?.slot
            );
            return (
              <button
                className={`btn fg-btn fg-btn-small ${resourceActiveClass(item, resource.id)} ${item?.disable || priceLoader ? "disabled" : ""}`}
                key={item?.slot}
                onClick={() => {
                  handleSlotClick(item, resource.id);
                }}
              >
                {isFinanceConfigured &&
                  priceDetails?.priceConfigured &&
                  (priceLoader ? (
                    <div className="fg-price-skeleton d-flex gap-1 mb-1">
                      <Skeleton variant="text" width={30} height={20} />
                      <Skeleton variant="text" width={32} height={20} />
                    </div>
                  ) : (
                    <div className="mb-1">
                      {resourcePrices?.currency &&
                        " " + resourcePrices?.currency}{" "}
                      {priceDetails?.price?.toFixed(DECIMAL_PLACES)}
                    </div>
                  ))}
                <span>
                  {item.slot}
                  {typeof item.slot === DataTypes.number && t("min")}
                </span>
              </button>
            );
          })}
        </div>
        {durations?.length > 0 &&
          (warningMessage.quote.length > 0 ||
            warningMessage.advanceBookingMsg) && (
            <div className="mt-2 mb-3">
              <div className="alert alert-warning p-2 fg-adv-booking-msg">
                {warningMessage.quote.length > 0
                  ? warningMessage.quote.map((item, index) => (
                      <div
                        key={item}
                        className={
                          index < warningMessage.quote.length - 1 ? "mb-2" : ""
                        }
                      >
                        {item}
                      </div>
                    ))
                  : warningMessage.advanceBookingMsg}
              </div>
            </div>
          )}
      </div>
    </div>
  );
};

export default ResourceDuration;
